import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'
import PersonSearchIcon from '@mui/icons-material/PersonSearch'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import TableViewIcon from '@mui/icons-material/TableView'
import React from 'react'
import {
  API_PATH_ADMIN_CUSTOMER_SEARCH,
  ADMIN_ALL_UNITS_ROUTE,
  ADMIN_CANCELLATIONS_ROUTE,
  ADMIN_SUBSCRIPTIONS_ROUTE,
} from '../../config'
import { getApiConfig, getLocalServerURL } from '../../utils/getApiConfig'

export type AdminWorkflowKey =
  | 'createCustomerRental'
  | 'createCustomerProfile'
  | 'createTransaction'
  | 'createSubscription'
  | 'cancelSubscription'

type AdminCommandOption =
  | {
      type: 'customer'
      key: string
      group: 'Customers'
      customerProfileId: number
      customerName: string
      email: string
      phoneNumber: string
      portalAccessState: string
      label: string
      description: string
    }
  | {
      type: 'workflow'
      key: AdminWorkflowKey
      group: 'Workflows' | 'Selected Customer'
      label: string
      description: string
    }
  | {
      type: 'report'
      key: 'audit' | 'batchSettlement'
      group: 'Reports'
      label: string
      description: string
    }
  | {
      type: 'view'
      key: 'subscriptions' | 'allUnits' | 'cancellations'
      group: 'Views'
      label: string
      description: string
      route: string
    }

interface Props {
  seedCustomers?: Array<{
    customerProfileId: number
    customerName: string
    email: string
    phoneNumber: string
    portalAccessState?: string
  }>
  onSelectCustomer: (customer: {
    customerProfileId: number
    customerName: string
  }) => void
  selectedCustomer?: {
    customerProfileId: number
    customerName: string
  } | null
  onSelectReport: (report: 'audit' | 'batchSettlement') => void
  onSelectView: (route: string) => void
  onSelectWorkflow: (workflow: AdminWorkflowKey) => void
}

const normalizeSearchValue = (value?: string | null) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

const GLOBAL_OPTIONS: AdminCommandOption[] = [
  {
    type: 'workflow',
    key: 'createCustomerRental',
    group: 'Workflows',
    label: 'Create customer + rentals',
    description: 'Run the phone-order onboarding workflow and generate claim access.',
  },
  {
    type: 'workflow',
    key: 'createCustomerProfile',
    group: 'Workflows',
    label: 'Create customer profile only',
    description: 'Create a stored customer profile without assigning rentals.',
  },
  {
    type: 'report',
    key: 'audit',
    group: 'Reports',
    label: 'View audit report',
    description: 'Open the storage audit report viewer.',
  },
  {
    type: 'report',
    key: 'batchSettlement',
    group: 'Reports',
    label: 'Download batch settlement report',
    description: 'Prepare and download the settlement workbook.',
  },
  {
    type: 'view',
    key: 'subscriptions',
    group: 'Views',
    label: 'Subscriptions',
    description: 'Switch to the subscriptions table.',
    route: ADMIN_SUBSCRIPTIONS_ROUTE,
  },
  {
    type: 'view',
    key: 'allUnits',
    group: 'Views',
    label: 'All units',
    description: 'Switch to the all-units table.',
    route: ADMIN_ALL_UNITS_ROUTE,
  },
  {
    type: 'view',
    key: 'cancellations',
    group: 'Views',
    label: 'Cancellations',
    description: 'Switch to the cancellations table.',
    route: ADMIN_CANCELLATIONS_ROUTE,
  },
]

const getSelectedCustomerWorkflowOptions = (
  selectedCustomer?: {
    customerProfileId: number
    customerName: string
  } | null
): AdminCommandOption[] => {
  if (selectedCustomer == null) {
    return []
  }

  const customerLabel =
    selectedCustomer.customerName || `Customer #${selectedCustomer.customerProfileId}`

  return [
    {
      type: 'workflow',
      key: 'createTransaction',
      group: 'Selected Customer',
      label: 'Create one-time transaction',
      description: `Charge ${customerLabel} from the customer workbench.`,
    },
    {
      type: 'workflow',
      key: 'createSubscription',
      group: 'Selected Customer',
      label: 'Create subscription for selected customer',
      description: `Open subscriptions for ${customerLabel}.`,
    },
    {
      type: 'workflow',
      key: 'cancelSubscription',
      group: 'Selected Customer',
      label: 'Cancel selected customer subscription',
      description: `Open subscription management for ${customerLabel}.`,
    },
  ]
}

const getOptionIcon = (option: AdminCommandOption) => {
  switch (option.group) {
    case 'Customers':
      return <PersonSearchIcon fontSize="small" />
    case 'Workflows':
      return <ManageAccountsIcon fontSize="small" />
    case 'Reports':
      return <ReceiptLongIcon fontSize="small" />
    case 'Views':
    default:
      return <TableViewIcon fontSize="small" />
  }
}

export const AdminCommandBar: React.FC<Props> = ({
  seedCustomers = [],
  onSelectCustomer,
  selectedCustomer,
  onSelectReport,
  onSelectView,
  onSelectWorkflow,
}) => {
  const [inputValue, setInputValue] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [customerOptions, setCustomerOptions] = React.useState<
    AdminCommandOption[]
  >([])

  const localCustomerOptions = React.useMemo(
    () =>
      seedCustomers.map((customer) => ({
        type: 'customer' as const,
        key: `customer-${customer.customerProfileId}`,
        group: 'Customers' as const,
        customerProfileId: customer.customerProfileId,
        customerName:
          customer.customerName || `Customer #${customer.customerProfileId}`,
        email: customer.email,
        phoneNumber: customer.phoneNumber,
        portalAccessState: customer.portalAccessState ?? 'unclaimed',
        label:
          customer.customerName || `Customer #${customer.customerProfileId}`,
        description: [
          customer.phoneNumber,
          customer.email,
          customer.portalAccessState ?? 'unclaimed',
        ]
          .filter(Boolean)
          .join(' · '),
      })),
    [seedCustomers]
  )

  React.useEffect(() => {
    const query = inputValue.trim()

    if (query.length < 2) {
      setCustomerOptions([])
      setLoading(false)
      return
    }

    let isActive = true
    const timeoutId = window.setTimeout(async () => {
      setLoading(true)
      try {
        const config = await getApiConfig({
          body: { query },
        })

        if (config == null) {
          if (isActive) {
            setCustomerOptions([])
          }
          return
        }

        const response = await fetch(
          getLocalServerURL(API_PATH_ADMIN_CUSTOMER_SEARCH),
          config
        )

        if (!response.ok) {
          if (isActive) {
            setCustomerOptions([])
          }
          return
        }

        const data = (await response.json()) as {
          customers?: Array<{
            customerProfileId: number
            customerName: string
            email: string
            phoneNumber: string
            portalAccessState: string
          }>
        }

        if (!isActive) {
          return
        }

        setCustomerOptions(
          (data.customers ?? []).map((customer) => ({
            type: 'customer',
            key: `customer-${customer.customerProfileId}`,
            group: 'Customers',
            customerProfileId: customer.customerProfileId,
            customerName: customer.customerName || `Customer #${customer.customerProfileId}`,
            email: customer.email,
            phoneNumber: customer.phoneNumber,
            portalAccessState: customer.portalAccessState,
            label: customer.customerName || `Customer #${customer.customerProfileId}`,
            description: [
              customer.phoneNumber,
              customer.email,
              customer.portalAccessState,
            ]
              .filter(Boolean)
              .join(' · '),
          }))
        )
      } finally {
        if (isActive) {
          setLoading(false)
        }
      }
    }, 250)

    return () => {
      isActive = false
      window.clearTimeout(timeoutId)
    }
  }, [inputValue])

  const options = React.useMemo(() => {
    const uniqueCustomers = new Map<string, AdminCommandOption>()

    for (const customerOption of [...localCustomerOptions, ...customerOptions]) {
      uniqueCustomers.set(customerOption.key, customerOption)
    }

    return [
      ...uniqueCustomers.values(),
      ...GLOBAL_OPTIONS,
      ...getSelectedCustomerWorkflowOptions(selectedCustomer),
    ]
  }, [customerOptions, localCustomerOptions, selectedCustomer])

  return (
    <Autocomplete<AdminCommandOption, false, false, false>
      options={options}
      value={null}
      groupBy={(option) => option.group}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) =>
        option.group === value.group && option.key === value.key
      }
      filterOptions={(availableOptions, state) => {
        const normalizedInput = normalizeSearchValue(state.inputValue)

        if (normalizedInput === '') {
          return availableOptions
        }

        return availableOptions.filter((option) =>
          normalizeSearchValue(
            `${option.label} ${option.description ?? ''}`
          ).includes(normalizedInput)
        )
      }}
      inputValue={inputValue}
      onInputChange={(_event, value) => {
        setInputValue(value)
      }}
      onChange={(_event, option) => {
        if (option == null) return

        if (option.type === 'customer') {
          onSelectCustomer({
            customerProfileId: option.customerProfileId,
            customerName: option.customerName,
          })
        } else if (option.type === 'workflow') {
          onSelectWorkflow(option.key)
        } else if (option.type === 'report') {
          onSelectReport(option.key)
        } else if (option.type === 'view') {
          onSelectView(option.route)
        }

        setInputValue('')
      }}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Customer, Report, or Action"
          placeholder="Search by name, phone, email, report, or workflow"
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={`${option.group}-${option.key}`}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              py: 0.25,
            }}
          >
            <Box sx={{ color: 'text.secondary', mt: 0.25 }}>
              {getOptionIcon(option)}
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={700}>
                {option.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {option.description}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
      noOptionsText={
        inputValue.trim().length < 2
          ? 'Type at least 2 characters to search customers.'
          : 'No customers or actions matched.'
      }
      sx={{
        minWidth: { xs: '100%', lg: 460 },
      }}
    />
  )
}
